import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, BookOpen, User, Settings, LogOut, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '../context/SessionContext';

const Profile = () => {
  const navigate = useNavigate();
  const { birthDetails: sessionBirthDetails, user, logout, isAuthenticated } = useSession();
  const [activeTab, setActiveTab] = useState('profile');
  const [birthDetails, setBirthDetails] = useState(null);

  useEffect(() => {
    // Use birth details from SessionContext (MongoDB) first
    if (sessionBirthDetails) {
      setBirthDetails(sessionBirthDetails);
    } else {
      // Fallback to localStorage only if session doesn't have it
      const details = localStorage.getItem('birthDetails');
      if (details) {
        setBirthDetails(JSON.parse(details));
      }
    }
  }, [sessionBirthDetails]);

  const handleLogout = async () => {
    try {
      // Use SessionContext logout which clears everything properly
      await logout();
      toast.success('Logged out successfully');
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };

  const handleResetTest = () => {
    localStorage.removeItem('story_unlocked');
    localStorage.removeItem('health_audio_url');
    localStorage.removeItem('health_prediction');
    localStorage.removeItem('health_timeline');
    localStorage.removeItem('health_diseases');
    localStorage.removeItem('prediction_id');
    localStorage.removeItem('health_timeline_data');
    localStorage.removeItem('timeline_data_cache');
    localStorage.removeItem('detailed_monthly_data');
    localStorage.removeItem('detailed_yearly_report');
    localStorage.removeItem('domain_report_cache');
    toast.success('Test data cleared! Restart from beginning.');
  };

  const menuItems = [
    { icon: <Settings />, label: 'Settings', action: () => toast.info('Settings coming soon') },
    { icon: <ChevronRight />, label: 'Edit Birth Details', action: () => navigate('/birth-details') },
    { icon: <ChevronRight />, label: 'Reset Test Data', action: handleResetTest, warning: true },
    { icon: <ChevronRight />, label: 'Notifications', action: () => toast.info('Notifications coming soon') },
    { icon: <ChevronRight />, label: 'Privacy Policy', action: () => toast.info('Privacy Policy') },
    { icon: <ChevronRight />, label: 'Terms of Service', action: () => toast.info('Terms of Service') },
    { icon: <LogOut />, label: 'Logout', action: handleLogout, danger: true }
  ];

  return (
    <div className="min-h-screen pb-24" data-testid="profile-screen">
      {/* Header with Profile */}
      <div className="p-6 pb-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg viewBox="0 0 400 200" className="w-full h-full">
            <circle cx="200" cy="100" r="80" stroke="var(--gold)" strokeWidth="1" fill="none" />
          </svg>
        </div>

        <div className="relative z-10">
          <div className="w-24 h-24 rounded-full mx-auto mb-4 border-2 border-gold overflow-hidden">
            <img 
              src="/taraanaya-logo.jpeg" 
              alt="Profile" 
              className="w-full h-full object-cover"
              data-testid="profile-avatar"
            />
          </div>
          <h1 className="text-2xl mb-1" style={{fontFamily: 'Playfair Display', color: 'var(--gold)'}} data-testid="profile-name">
            {birthDetails?.name || user?.name || 'User'}
          </h1>
          <p className="text-gray-400 text-sm">{user?.email || 'Member since 2024'}</p>
        </div>
      </div>

      {/* Birth Details Card */}
      {birthDetails && (
        <div className="px-6 mb-8">
          <div className="card-astro">
            <h2 className="text-lg mb-4" style={{fontFamily: 'Playfair Display', color: 'var(--gold)'}}>Birth Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Date of Birth</span>
                <span className="text-white font-medium">{birthDetails.dateOfBirth || birthDetails.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Time of Birth</span>
                <span className="text-white font-medium">{birthDetails.timeOfBirth || birthDetails.time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Place</span>
                <span className="text-white font-medium">{birthDetails.placeOfBirth || birthDetails.place}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Items */}
      <div className="px-6">
        <div className="space-y-2">
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              onClick={item.action}
              className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors ${
                item.danger 
                  ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400' 
                  : item.warning
                  ? 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400'
                  : 'bg-white/5 hover:bg-white/10 text-white'
              }`}
              data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center gap-3">
                {item.icon}
                <span>{item.label}</span>
              </div>
              <ChevronRight className="w-5 h-5 opacity-50" />
            </button>
          ))}
        </div>
      </div>

      {/* App Info */}
      <div className="px-6 mt-8 text-center text-gray-500 text-sm">
        <p>Taraanaya v1.0.0</p>
        <p className="mt-2">Taraanaya 2026. All rights reserved.</p>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/dashboard" className={`bottom-nav-item ${activeTab === 'home' ? 'active' : ''}`} data-testid="nav-home">
          <Home className="w-6 h-6" />
          <span>Home</span>
        </Link>
        <Link to="/my-story" className={`bottom-nav-item ${activeTab === 'story' ? 'active' : ''}`} data-testid="nav-story">
          <BookOpen className="w-6 h-6" />
          <span>My Story</span>
        </Link>
        <Link to="/profile" className={`bottom-nav-item ${activeTab === 'profile' ? 'active' : ''}`} data-testid="nav-profile">
          <User className="w-6 h-6" />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default Profile;
